import * as React from 'react';
import { css, keyframes, SerializedStyles } from '@emotion/core';
import { COLORS, CHART_COLORS } from '../globalStyle/colors';
import { useBrowser } from '../language/useBrowser';


const styles = {
  root:           css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  svgRoot:        css`
    position: absolute;
    z-index: 1;
    transform: rotate(-90deg); /* The unit circle starts at 3 o'clock, this rotates it over to 12 o'clock */
  `,
  childContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 2;
  `,
};


export type RingConfig = {
  /**
   * Used to uniquely ID the ring
   */
  name: string | number;
  color: string;
  progress: number;
};


type SvgConfig = {
  normalizedProgress: number;
  strokeDashoffset: number;
  style: SerializedStyles
};


type Props = {
  ringConfig: RingConfig[];
  radius: number;

  // Default Props
  stroke?: number;
  ringBg?: string;
  insideColor?: string;

  className?: string;
};


const toSvgConfig = (circumference: number) => (config: RingConfig): RingConfig & SvgConfig => {
  const normalizedProgress = config.progress > 1 ? config.progress / 100 : config.progress;
  const strokeDashOffest   = circumference - normalizedProgress * circumference;

  const circleAnimation = keyframes`
    from {
      stroke-dashoffset: ${circumference};
    }
    to {
      stroke-dashoffset: ${strokeDashOffest};
    }
  `;

  return ({
    ...config,
    normalizedProgress: normalizedProgress,
    strokeDashoffset:   strokeDashOffest,
    style:              css`
      animation: ${circleAnimation} 600ms ease-in;
      transition: all 600ms ease; /* In case the value changes */
    `,
  });
};


/**
 * ProgressCircle: a component that renders progress in the form of a circle, and children inside that progress circle.
 * involves some position: absolute sorcery, unfortunately...
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ProgressCircle: React.FC<Props> = props => {
  const { onIe } = useBrowser();

  // IE11 needs lots of help to properly position the outer rings
  const ieSvgContainerFix = onIe
    ? css`
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      margin: auto;
      width: ${2 * props.radius}px;
      height: ${2 * props.radius}px;
    `
    : undefined;

  // The circle stroke centers over the circle border -- we have to subtract out the half-stroke that will poke out from
  // the circle's edge to make the total width/height match 2 * radius
  const normalizedRadius = React.useMemo(() => props.radius - props.stroke! / 2, [props.radius, props.stroke]);
  const circumference    = React.useMemo(() => 2 * Math.PI * normalizedRadius, [normalizedRadius]);

  const rings = React.useMemo(
    () => props.ringConfig.sort((a, b) => b.progress - a.progress).map(toSvgConfig(circumference)),
    [props.ringConfig, circumference],
  );

  return (
    <div css={styles.root} className={props.className}>
      <svg
        height={2 * props.radius}
        width={2 * props.radius}
        css={[
          styles.svgRoot,
          ieSvgContainerFix,
        ]}
      >
        <circle
          stroke={props.ringBg}
          fill={props.insideColor}
          strokeWidth={props.stroke}
          r={normalizedRadius}
          cx={props.radius}
          cy={props.radius}
        />
        {
          rings.map(
            ring => (
              <circle
                key={ring.name}
                stroke={ring.color}
                fill='transparent'
                strokeWidth={props.stroke}
                strokeDasharray={`${circumference} ${circumference}`}
                style={{ strokeDashoffset: ring.strokeDashoffset }}
                r={normalizedRadius}
                cx={props.radius}
                cy={props.radius}
                css={ring.style}
              />
            ),
          )
        }
      </svg>
      <div css={styles.childContainer}>
        {props.children}
      </div>
    </div>
  );
};

ProgressCircle.defaultProps = {
  insideColor: COLORS.WHITE,
  ringBg:      CHART_COLORS.RING_BG,
  stroke:      10,
};

export default ProgressCircle;
